import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import KOREA_REGIONS from "../data/koreaDistricts";
import { useI18n } from "../contexts/I18nContext";

const ICONS = {
  shopping: (<svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.064 60.064 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>),
  rental: (<svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 12h.008v.008h-.008V12zm-2.25 0h.008v.008H16.5V12z" /></svg>),
  support: (<svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>),
  account: (<svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>),
};

export default function SitemapPage() {
  const { t } = useI18n();

  const SECTION = [
    {
      title: t.sitemap_shopping,
      icon: ICONS.shopping,
      links: [
        { label: t.sitemap_allProducts, to: "/store" },
        { label: t.sitemap_compareLink, to: "/compare" },
      ],
    },
    {
      title: t.sitemap_rentalSection,
      icon: ICONS.rental,
      links: [
        { label: t.sitemap_nationalRental, to: "/rental" },
        { label: t.sitemap_rentalConsult, to: "/consultation" },
      ],
    },
    {
      title: t.sitemap_support,
      icon: ICONS.support,
      links: [
        { label: t.sitemap_quoteInquiry, to: "/consultation" },
        { label: t.sitemap_phoneConsult, href: "tel:1600-7542" },
      ],
    },
    {
      title: "이용 안내",
      icon: ICONS.account,
      links: [
        { label: "교환·반품 정책", to: "/policy" },
        { label: "복합기 선택 가이드", to: "/trend" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <SEO title={t.sitemap_seoTitle} description={t.sitemap_seoDesc} path="/sitemap" />

      <div className="max-w-[1100px] mx-auto px-4 pt-20 pb-24">
        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-[32px] md:text-[40px] font-bold text-[#1d1d1f] tracking-tight">
            {t.sitemap_heading}
          </h1>
          <p className="text-[15px] text-gray-500 mt-2">
            {t.sitemap_subtitle}
          </p>
        </div>

        {/* Main sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {SECTION.map((s) => (
            <div
              key={s.title}
              className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition"
            >
              <div className="flex items-center gap-2 mb-4">
                <span>{s.icon}</span>
                <h2 className="text-[15px] font-bold text-[#1d1d1f]">
                  {s.title}
                </h2>
              </div>
              <ul className="space-y-2">
                {s.links.map((l) =>
                  l.to ? (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className="text-[13px] text-gray-600 hover:text-[#0071e3] transition"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-[13px] text-gray-600 hover:text-[#0071e3] transition"
                      >
                        {l.label}
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Regional rental directory */}
        <div className="mb-8">
          <h2 className="text-[20px] font-bold text-[#1d1d1f] mb-1 flex items-center gap-2">
            <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
            {t.sitemap_regionalTitle}
          </h2>
          <p className="text-[13px] text-gray-500 mb-6">
            {t.sitemap_regionalDesc.replace('{count}', KOREA_REGIONS.reduce((s, r) => s + r.districts.length, 0))}
          </p>
        </div>

        <div className="space-y-6">
          {KOREA_REGIONS.map((r) => (
            <div
              key={r.short}
              className="bg-white rounded-2xl border border-gray-200 p-6"
            >
              <Link
                to={`/rental/${encodeURIComponent(r.short)}`}
                className="inline-flex items-center gap-2 mb-4 group"
              >
                <h3 className="text-[15px] font-bold text-[#1d1d1f] group-hover:text-[#0071e3] transition">
                  {r.region}
                </h3>
                <span className="text-[11px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {r.districts.length}{t.sitemap_districtCount}
                </span>
                <span className="text-[11px] text-[#0071e3] opacity-0 group-hover:opacity-100 transition">
                  {t.sitemap_viewAll}
                </span>
              </Link>
              <div className="flex flex-wrap gap-2">
                {r.districts.map((d) => (
                  <Link
                    key={d.name}
                    to={`/rental/${encodeURIComponent(r.short)}/${encodeURIComponent(d.name)}`}
                    className="px-3 py-1.5 bg-[#f5f5f7] border border-gray-100 rounded-full text-[12px] text-gray-600 hover:border-[#0071e3] hover:text-[#0071e3] hover:bg-blue-50 transition"
                  >
                    {d.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl border border-gray-200 p-10">
            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">
              {t.sitemap_ctaTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {t.sitemap_ctaSub}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link
                to="/inquiry"
                className="flex items-center gap-2 px-6 py-3 bg-[#0071e3] text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                {t.sitemap_ctaBtn}
              </Link>
              <a
                href="tel:1600-7542"
                className="flex items-center gap-2 px-6 py-3 bg-[#1d1d1f] text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                1600-7542
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
